import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Upload,
  Space,
  Switch,
  Tag
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { backgroundImages, upload, proxyImageUrl } from '../../api';

const { TextArea } = Input;
const ADMIN_BACKGROUND_THUMBNAIL_OPTIONS = { width: 360, height: 300, mode: 'lfit', quality: 82 };

function BackgroundImageManage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [batchFileList, setBatchFileList] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [form] = Form.useForm();
  const [batchForm] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, [keyword]);

  const fetchData = async () => {
    setLoading(true);

    try {
      const res = await backgroundImages.list({
        include_disabled: 1,
        keyword: keyword || undefined
      });
      setData(res.data.data || []);
    } catch (err) {
      message.error('获取背景图失败');
    }

    setLoading(false);
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ sort_order: 0, enabled: true });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleBatchAdd = () => {
    batchForm.resetFields();
    batchForm.setFieldsValue({ sort_order: 0, enabled: true });
    setBatchFileList([]);
    setBatchModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await backgroundImages.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleEnabledChange = async (record, enabled) => {
    try {
      await backgroundImages.update(record.id, {
        ...record,
        enabled
      });
      message.success(enabled ? '已启用' : '已停用');
      fetchData();
    } catch (err) {
      message.error('状态更新失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingItem) {
        await backgroundImages.update(editingItem.id, values);
        message.success('更新成功');
      } else {
        await backgroundImages.create(values);
        message.success('添加成功');
      }

      setModalVisible(false);
      fetchData();
    } catch (err) {
      if (err?.errorFields) {
        return;
      }

      message.error(err.response?.data?.message || '操作失败');
    }
  };

  const handleBatchSubmit = async () => {
    try {
      const values = await batchForm.validateFields();

      if (!batchFileList.length) {
        message.error('请先选择背景图');
        return;
      }

      message.loading({ content: '正在上传背景图...', key: 'background-image-batch' });
      const items = [];

      for (const file of batchFileList) {
        try {
          const res = await upload.upload(file.originFileObj || file);
          items.push({
            title: values.title,
            image_url: res.data.data.url
          });
        } catch (err) {
          message.error({ content: `上传失败: ${file.name}`, key: 'background-image-batch' });
          return;
        }
      }

      const payload = {
        items,
        description: values.description,
        sort_order: values.sort_order,
        enabled: values.enabled
      };

      const res = await backgroundImages.batchCreate(payload);
      message.success({ content: `成功添加 ${res.data.data.count} 张背景图`, key: 'background-image-batch' });
      setBatchModalVisible(false);
      setBatchFileList([]);
      fetchData();
    } catch (err) {
      if (err?.errorFields) {
        return;
      }

      message.error(err.response?.data?.message || '操作失败');
    }
  };

  const handleUpload = async (file) => {
    try {
      const res = await upload.upload(file);
      form.setFieldsValue({ image_url: res.data.data.url });
      message.success('上传成功');
    } catch (err) {
      message.error('上传失败');
    }

    return false;
  };

  const handleBatchUploadChange = ({ fileList }) => {
    setBatchFileList(fileList);
  };

  const handleSearch = (value) => {
    const nextKeyword = String(value || '').trim();
    setSearchInput(value);
    setKeyword(nextKeyword);
  };

  return (
    <div>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card>
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>背景图</div>
            <div style={{ color: '#667085' }}>
              后台上传背景图，供编辑器左侧“背景”面板选择使用。
            </div>
            <Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ width: 160 }}>
                添加背景图
              </Button>
              <Button icon={<UploadOutlined />} onClick={handleBatchAdd} style={{ width: 160 }}>
                批量添加
              </Button>
              <Input.Search
                allowClear
                placeholder="搜索标题或说明"
                value={searchInput}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setSearchInput(nextValue);

                  if (!nextValue) {
                    handleSearch('');
                  }
                }}
                onSearch={handleSearch}
                style={{ width: 280 }}
              />
            </Space>
          </Space>
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}>
          {data.map((item) => (
            <Card
              key={item.id}
              loading={loading}
              hoverable
              cover={
                <div style={{ height: 220, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                  <img
                    alt={item.title}
                    src={proxyImageUrl(item.image_url, ADMIN_BACKGROUND_THUMBNAIL_OPTIONS)}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </div>
              }
              actions={[
                <Button key="edit" type="link" icon={<EditOutlined />} onClick={() => handleEdit(item)}>
                  编辑
                </Button>,
                <Popconfirm key="delete" title="确定删除这张背景图？" onConfirm={() => handleDelete(item.id)}>
                  <Button type="link" danger icon={<DeleteOutlined />}>
                    删除
                  </Button>
                </Popconfirm>
              ]}
            >
              <Space direction="vertical" size={10} style={{ width: '100%' }}>
                <div style={{ fontSize: 16, fontWeight: 600 }}>{item.title}</div>
                <div style={{ color: '#667085', minHeight: 42 }}>{item.description || '暂无说明'}</div>
                <div>
                  <Tag color={item.enabled === false ? 'default' : 'success'}>
                    {item.enabled === false ? '已停用' : '已启用'}
                  </Tag>
                </div>
                <Switch
                  checked={item.enabled !== false}
                  checkedChildren="启用"
                  unCheckedChildren="停用"
                  onChange={(checked) => handleEnabledChange(item, checked)}
                />
                <div style={{ color: '#98a2b3', wordBreak: 'break-all' }}>{item.image_url}</div>
              </Space>
            </Card>
          ))}
        </div>

        {!data.length && !loading && (
          <Card>
            <div style={{ textAlign: 'center', color: '#98a2b3', padding: '36px 0' }}>还没有背景图，先上传一些背景图。</div>
          </Card>
        )}
      </Space>

      <Modal
        title={editingItem ? '编辑背景图' : '添加背景图'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={680}
        maskClosable={false}
        keyboard={false}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="例如：瑞华并联机组产品图" />
          </Form.Item>

          <Form.Item name="description" label="说明">
            <TextArea rows={3} placeholder="可填写背景图简介、产品说明等" />
          </Form.Item>

          <Form.Item name="sort_order" label="排序值">
            <Input type="number" placeholder="数值越大越靠前" />
          </Form.Item>

          <Form.Item name="enabled" label="前台显示" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>

          <Form.Item name="image_url" label="背景图地址" rules={[{ required: true, message: '请上传背景图' }]}>
            <Input
              placeholder="https://img2.zhilengwang.cn/..."
              addonBefore={
                <Upload showUploadList={false} beforeUpload={handleUpload} accept="image/*">
                  <Button icon={<UploadOutlined />}>上传</Button>
                </Upload>
              }
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="批量添加背景图"
        open={batchModalVisible}
        onOk={handleBatchSubmit}
        onCancel={() => setBatchModalVisible(false)}
        width={680}
        okText="批量添加"
        maskClosable={false}
        keyboard={false}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <Form form={batchForm} layout="vertical">
          <Form.Item name="title" label="统一标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="例如：瑞华产品图" />
          </Form.Item>

          <Form.Item name="description" label="统一说明">
            <TextArea rows={3} placeholder="可统一填写这批背景图的简介或产品说明" />
          </Form.Item>

          <Form.Item name="sort_order" label="统一排序值">
            <Input type="number" placeholder="数值越大越靠前" />
          </Form.Item>

          <Form.Item name="enabled" label="前台显示" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>

          <Form.Item
            name="image_urls"
            label="上传背景图"
            rules={[{ required: true, message: '请上传背景图' }]}
          >
            <Upload.Dragger
              listType="picture-card"
              multiple
              accept="image/*"
              beforeUpload={() => false}
              onChange={handleBatchUploadChange}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p>点击或拖拽背景图到此区域</p>
              <p style={{ color: '#999', fontSize: 12 }}>
                支持批量上传，这批背景图会先使用同一个标题，后续可单独编辑
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default BackgroundImageManage;
